import {Injectable} from '@angular/core';
import {BehaviorSubject, firstValueFrom, Observable, of} from 'rxjs';
import {tap} from 'rxjs/operators';
import {Movimento} from '../_models/Movimento';
import {HubConnection} from '@microsoft/signalr';
import {buildHubConnection, startHubConnection, stopHubConnection} from '../_helpers/hub-connection.helper';
import {HubState} from '../_models/hub-state';
import {HttpClient, HttpParams} from '@angular/common/http';
import {SaveMessage} from '../_helpers/save-message';
import {OrganizzazioniService} from './organizzazioni.service';
import {environment} from '../../environments/environment';
import {SearchResult} from '../_models/SearchResult';
import {Giacenza} from '../_models/Giacenza';
import {Contenitore} from '../_models/Contenitore';
import {MovimentoConsumoField, MovimentoConsumoOption} from '../_models/MovimentoConsumoOption';
import {ContenitoreFormato} from '../_models/ContenitoreFormato';
import {InventarioRiga} from "../_models/InventarioRiga";
import {AuthenticationService} from './authentication.service';
import {ImportAnalysisResult} from "../_models/ImportAnalysisResult";
import {ReportResult} from "../_models/ReportResult";

interface CachedConsumatoOptions {
  organizzazioneId: number;
  result: SearchResult<MovimentoConsumoOption>;
  expiresAt: number;
}

@Injectable({providedIn: 'root'})
export class MovimentiService {
  public lastMovimentoCreated: BehaviorSubject<Movimento> = new BehaviorSubject<Movimento>(null);
  public lastMovimentoUpdated: BehaviorSubject<Movimento> = new BehaviorSubject<Movimento>(null);
  public lastMovimentoDeleted: BehaviorSubject<Movimento> = new BehaviorSubject<Movimento>(null);
  public hubState: BehaviorSubject<number> = new BehaviorSubject<number>(HubState.Connecting);
  // Guard chirurgico (feature 052 / FR-003): impostato a true SOLO mentre la modale di
  // rettifica giacenza è aperta. Sopprime esclusivamente il toast globale invasivo sui
  // movimenti creati mentre quella modale è in uso; la notifica per il resto dell'app
  // (lastMovimentoCreated e gli altri eventi) resta invariata.
  public rettificaModalAperta = false;
  private static readonly consumatoOptionsCacheTtlMs = 2 * 60 * 1000;
  private readonly consumatoOptionsCache = new Map<string, CachedConsumatoOptions>();
  private readonly formatiCache = new Map<number, Promise<ContenitoreFormato[]>>();
  private hubConnection: HubConnection;

  constructor(
    private http: HttpClient,
    private authService: AuthenticationService,
    private organizzazioniService: OrganizzazioniService,
    private saveMessage: SaveMessage) {}

  public startConnection() {
    this.hubConnection = buildHubConnection(`${environment.localApiUrl}hubs/movimento`, this.hubState);

    this.hubConnection.on('SendCreated', (movimento: Movimento) => this.handleMovimentoCreated(movimento));

    this.hubConnection.on('SendUpdated', (movimento: Movimento) => {
      if (movimento.organizzazioneId === this.organizzazioniService.organizzazioneId) {
        this.handleMovimentoUpdated(movimento);
      }
    });

    this.hubConnection.on('SendDeleted', (movimento: Movimento) => {
      if (movimento.organizzazioneId === this.organizzazioniService.organizzazioneId) {
        this.invalidateConsumatoOptionsCache(movimento.organizzazioneId);
        this.saveMessage.entityDeleted('movimento', movimento);
        this.lastMovimentoDeleted.next(movimento);
      }
    });

    return startHubConnection(this.hubConnection, this.hubState);
  }

  public stopConnection() {
    void stopHubConnection(this.hubConnection);
  }

  // Gestione notifica "movimento creato" via SignalR. Estratta per testabilità del guard
  // chirurgico (feature 052 / FR-003): mentre la modale di rettifica è aperta, il toast
  // globale è soppresso ma lo stream lastMovimentoCreated continua a notificare l'app.
  public handleMovimentoCreated(movimento: Movimento) {
    if (movimento.organizzazioneId !== this.organizzazioniService.organizzazioneId) {
      return;
    }
    // Un nuovo movimento può introdurre un articolo o un OdS finora assente dalle tendine.
    this.invalidateConsumatoOptionsCache(movimento.organizzazioneId);
    if (!this.rettificaModalAperta) {
      this.saveMessage.entityCreated('movimento', movimento);
    }
    this.lastMovimentoCreated.next(movimento);
  }

  // Un movimento viene creato senza data e registrato DOPO, con un aggiornamento
  // (MovimentoMqWorker valorizza DataRegistrazione e notifica SendUpdated): è proprio in quel
  // momento che diventa un consumo avvenuto e quindi eleggibile per le tendine. Senza questa
  // invalidazione resterebbe invisibile fino alla scadenza della cache.
  public handleMovimentoUpdated(movimento: Movimento) {
    this.invalidateConsumatoOptionsCache(movimento.organizzazioneId);
    this.saveMessage.entityUpdated('movimento', movimento);
    this.lastMovimentoUpdated.next(movimento);
  }

  // rifDoc filtra l'ordine di PRODUZIONE (RifOdP), rifDocumento l'ordine di SPEDIZIONE (RifDocumento):
  // sono due riferimenti distinti sullo stesso movimento.
  async getMovimentiFilteredAsync(orgId: number, search: string, page: number, count: number, sortField: string, sortAsc: boolean,
                                  contesto: number, codArt: string, rifDoc: string, codMag: string, codArea: string, codLotto: string,
                                  codBox: string, startDate: Date, endDate: Date, tipoMovimento?: number, soloConsumato?: boolean,
                                  rifDocumento?: string) {
    return firstValueFrom(this.http.post<SearchResult<Movimento>>(`${environment.localApiUrl}api/Movimenti/MovimentiFiltered?orgId=${orgId}`,
      {search, page, count, sortField, sortAsc, contesto, tipoMovimento, codArt, rifDoc, codMag, codArea, codLotto, codBox, startDate, endDate, soloConsumato, rifDocumento}));
  }

  // Observable e non Promise: il combobox annulla la richiesta precedente a ogni digitazione.
  // La cache (stesso criterio di PesateArricchiteService) evita di riaggregare lo storico dei
  // movimenti a ogni riapertura della tendina: l'elenco dei valori esistenti cambia di rado.
  searchConsumatoOptions$(orgId: number, field: MovimentoConsumoField, search: string, count: number,
                          contesto: number, page = 0): Observable<SearchResult<MovimentoConsumoOption>> {
    const normalizedSearch = search.trim();
    const cacheKey = `${orgId}|${field}|${contesto}|${count}|${page}|${normalizedSearch.toLowerCase()}`;
    const cached = this.consumatoOptionsCache.get(cacheKey);
    if (cached && cached.expiresAt > Date.now()) return of(cached.result);
    if (cached) this.consumatoOptionsCache.delete(cacheKey);

    return this.http.post<SearchResult<MovimentoConsumoOption>>(
      `${environment.localApiUrl}api/Movimenti/ConsumatoOptions?orgId=${orgId}`,
      {field, search: normalizedSearch, page, count, contesto}
    ).pipe(tap(result => this.consumatoOptionsCache.set(cacheKey, {
      organizzazioneId: orgId,
      result,
      expiresAt: Date.now() + MovimentiService.consumatoOptionsCacheTtlMs
    })));
  }

  invalidateConsumatoOptionsCache(organizzazioneId?: number): void {
    if (organizzazioneId === undefined) {
      this.consumatoOptionsCache.clear();
      return;
    }

    for (const [key, entry] of this.consumatoOptionsCache) {
      if (entry.organizzazioneId === organizzazioneId) this.consumatoOptionsCache.delete(key);
    }
  }

  async getMovimentoAsync(orgId: number, id: number) {
    return firstValueFrom(this.http.get<Movimento>(`${environment.localApiUrl}api/Movimenti/Movimento?orgId=${orgId}&id=${id}`));
  }

  async getMovimentiOfEventoAsync(orgId: number, idEvento: number) {
    return firstValueFrom(this.http.get<SearchResult<Movimento>>(`${environment.localApiUrl}api/Movimenti/MovimentiOfEvento/${orgId}/${idEvento}`));
  }

  async getMovimentiOfOrdSpedAsync(orgId: number, idOrdSped: number, idOrdSpedRiga: number, tipoMovimenti: number, page: number, count: number) {
    return firstValueFrom(this.http.post<SearchResult<Movimento>>(`${environment.localApiUrl}api/Movimenti/MovimentiOfOrdSped/${orgId}/${idOrdSped}/${idOrdSpedRiga}`,
      {search: '', page, count, sortField: 'DataRegistrazione', sortAsc: false, tipoMovimenti}));
  }

  async getMovimentiByParentBoxAsync(orgId: number, parentBox: string, page: number, count: number) {
    return firstValueFrom(this.http.post<SearchResult<Movimento>>(`${environment.localApiUrl}api/Movimenti/MovimentiByParentBox/${orgId}`,
      {search: parentBox, page, count, sortField: 'DataRegistrazione', sortAsc: false}));
  }

  async createMovimentoAsync(movimento: Movimento) {
    movimento.executedBy ??= this.getNotificationActor();
    return firstValueFrom(this.http.post<Movimento>(`${environment.localApiUrl}api/Movimenti/CreateMovimento`, movimento));
  }

  async updateMovimentoAsync(movimento: Movimento) {
    movimento.executedBy ??= this.getNotificationActor();
    return firstValueFrom(this.http.post<Movimento>(`${environment.localApiUrl}api/Movimenti/UpdateMovimento`, movimento));
  }

  async deleteMovimentoAsync(orgId: number, id: number) {
    const executedBy = this.getNotificationActor();
    const actorQuery = executedBy ? `?executedBy=${encodeURIComponent(executedBy)}` : '';
    return firstValueFrom(this.http.get<any>(`${environment.localApiUrl}api/Movimenti/DeleteMovimento/${orgId}/${id}${actorQuery}`));
  }

  async getContenitoriFilteredAsync(orgId: number, search: string, page: number, count: number, sortField: string, sortAsc: boolean,
                                    contesto: number, codArt: string, rifDoc: string, codMag: string, codArea: string, codLotto: string, includeEmpty: boolean) {
    return firstValueFrom(this.http.post<SearchResult<Contenitore>>(`${environment.localApiUrl}api/Movimenti/ContenitoriFiltered?orgId=${orgId}`,
      {search, page, count, sortField, sortAsc, contesto, codArt, rifDoc, codMag, codArea, codLotto, includeEmpty}));
  }

  async getContenitoreAsync(orgId: number, codBox: string) {
    return firstValueFrom(this.http.get<Contenitore>(`${environment.localApiUrl}api/Movimenti/Contenitore?orgId=${orgId}&codBox=${codBox}`));
  }

  // validaCodice (issue #3594): chiede al server di rifiutare i codici che avvelenerebbero il contatore.
  // Lo passa solo il box-input; per gli altri chiamanti l'URL resta quello di prima.
  async checkContenitoreAsync(orgId: number, codBox: string, formato: number, validaCodice = false) {
    const valida = validaCodice ? '&validaCodice=true' : '';
    return firstValueFrom(this.http.get<Contenitore>(
      `${environment.localApiUrl}api/Movimenti/CheckContenitore?orgId=${orgId}&codBox=${encodeURIComponent(codBox)}&formato=${formato}${valida}`));
  }

  async createContenitoreAsync(contenitore: Contenitore): Promise<Contenitore> {
    contenitore.executedBy ??= this.getNotificationActor();
    return firstValueFrom(this.http.post<Contenitore>(`${environment.localApiUrl}api/Movimenti/CreateContenitore`, contenitore));
  }

  async updateContenitoreAsync(contenitore: Contenitore) {
    contenitore.executedBy ??= this.getNotificationActor();
    return firstValueFrom(this.http.post<Contenitore>(`${environment.localApiUrl}api/Movimenti/UpdateContenitore`, contenitore));
  }

  async deleteContenitoreAsync(orgId: number, id: number) {
    return firstValueFrom(this.http.get<any>(`${environment.localApiUrl}api/Movimenti/DeleteContenitore/${orgId}/${id}`));
  }

  async emptyContenitoreAsync(orgId: number, codBox: string) {
    const executedBy = this.getNotificationActor();
    const actorQuery = executedBy ? `&executedBy=${encodeURIComponent(executedBy)}` : '';
    return firstValueFrom(this.http.get<any>(`${environment.localApiUrl}api/Movimenti/EmptyContenitore?orgId=${orgId}&codBox=${codBox}${actorQuery}`));
  }

  async getGiacenzaByCodBoxAsync(orgId: number, codBox: string) {
    return firstValueFrom(this.http.post<Giacenza>(`${environment.localApiUrl}api/Movimenti/GetGiacenzaByCodBox?orgId=${orgId}`,
      {codBox}));
  }

  exportMovimenti(orgId: number, search: string, page: number, count: number, sortField: string, sortAsc: boolean,
                  contesto: number, codArt: string, rifDoc: string, codMag: string, codArea: string, codLotto: string,
                  codBox: string, startDate: Date, endDate: Date, group: boolean, tipoMovimento?: number) {
    this.http.post(`${environment.localApiUrl}api/Movimenti/ExportMovimentiFiltered?orgId=${orgId}`,
      {search, page, count, sortField, sortAsc, contesto, tipoMovimento, codArt, rifDoc, codMag, codArea, codLotto, codBox, startDate, endDate, group},
      {responseType: 'blob'})
      .subscribe(blob => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'movimenti.csv';
        a.click();
        window.URL.revokeObjectURL(url);
      });
  }

  analyzeMovimenti(orgId: number, tracciatoId: number, file: File) {

    const formData = new FormData();
    formData.append('file', file);
    formData.append('orgId', orgId.toString());
    formData.append('tracciatoId', tracciatoId.toString());

    return this.http.post<ImportAnalysisResult>(
      `${environment.localApiUrl}api/Movimenti/ImportaMovimentiAnalyze`,
      formData
    );
  }

  confirmMovimenti(report: ImportAnalysisResult) {
    return this.http.post(
      `${environment.localApiUrl}api/Movimenti/ImportaMovimentiConfirm`,
      {report}
    );
  }

  exportContenitori(orgId: number, search: string, page: number, count: number, sortField: string, sortAsc: boolean,
                    contesto: number, codArt: string, rifDoc: string, codMag: string, codArea: string, codLotto: string, codBox: string, group: boolean) {
    this.http.post(`${environment.localApiUrl}api/Movimenti/ContenitoriExport?orgId=${orgId}`,
      {search, page, count, sortField, sortAsc, contesto, codArt, rifDoc, codMag, codArea, codLotto, codBox, group},
      {responseType: 'blob'})
      .subscribe(blob => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'contenitori.csv';
        a.click();
        window.URL.revokeObjectURL(url);
      });
  }

  getInventario(orgId: number, startDate: number, endDate: number) {
    return firstValueFrom(this.http.get<InventarioRiga[]>(`${environment.localApiUrl}api/Movimenti/InventarioContenitori?orgId=${orgId}&startDate=${startDate}&endDate=${endDate}`));
  }

  async getContenitoriFormatiFilteredAsync(orgId: number, search: string, page: number, count: number, sortField: string, sortAsc: boolean) {
    return firstValueFrom(this.http.post<SearchResult<Contenitore>>(`${environment.localApiUrl}api/Movimenti/FormatiFiltered?orgId=${orgId}`,
      {search, page, count, sortField, sortAsc}));
  }

  async getContenitoreFormatoAsync(orgId: number, code: string) {
    return firstValueFrom(this.http.get<Contenitore>(`${environment.localApiUrl}api/Movimenti/Formato?orgId=${orgId}&codBox=${code}`));
  }

  async createContenitoreFormatoAsync(formato: ContenitoreFormato): Promise<ContenitoreFormato> {
    const creato = await firstValueFrom(this.http.post<Contenitore>(`${environment.localApiUrl}api/Movimenti/CreateFormato`, formato));
    this.invalidaFormatiCache(formato.organizzazioneId);
    return creato;
  }

  async updateContenitoreFormatoAsync(formato: ContenitoreFormato) {
    const aggiornato = await firstValueFrom(this.http.post<Contenitore>(`${environment.localApiUrl}api/Movimenti/UpdateFormato`, formato));
    this.invalidaFormatiCache(formato.organizzazioneId);
    return aggiornato;
  }

  async deleteContenitoreFormatoAsync(orgId: number, id: number) {
    const esito = await firstValueFrom(this.http.get<any>(`${environment.localApiUrl}api/Movimenti/DeleteFormato/${orgId}/${id}`));
    this.invalidaFormatiCache(orgId);
    return esito;
  }

  // Formati dell'organizzazione in cache (issue #3594): una sola richiesta per organizzazione, condivisa
  // dalle richieste concorrenti dei box-input; una richiesta fallita viene ritentata alla successiva.
  // Create/update/delete dallo stesso client invalidano la voce; le modifiche da altri dispositivi
  // valgono dal ricaricamento della pagina.
  getContenitoriFormatiCachedAsync(orgId: number): Promise<ContenitoreFormato[]> {
    const inCache = this.formatiCache.get(orgId);
    if (inCache) return inCache;
    const richiesta = this.getContenitoriFormatiFilteredAsync(orgId, '', 0, 0, 'codice', true)
      .then((risultato): ContenitoreFormato[] => risultato?.result ?? []);
    richiesta.catch(() => {
      if (this.formatiCache.get(orgId) === richiesta) this.formatiCache.delete(orgId);
    });
    this.formatiCache.set(orgId, richiesta);
    return richiesta;
  }

  async getContenitoreFormatoCachedAsync(orgId: number, formatoId: number): Promise<ContenitoreFormato | null> {
    if (!formatoId || formatoId <= 0) return null;
    const formati = await this.getContenitoriFormatiCachedAsync(orgId);
    return formati.find(f => f.id === formatoId) ?? null;
  }

  private invalidaFormatiCache(orgId: number): void {
    this.formatiCache.delete(orgId);
  }

  async ddtTrasferimentoInternoAsync(orgId: number, ddtType: string, movimenti: Movimento[]) {
    return firstValueFrom(this.http.post<any>(`${environment.localApiUrl}api/Movimenti/DdtTrasferimentoInterno`,
      {orgId, ddtType, movimenti}));
  }

  async getReportContenitore(orgId: number, codBox: string, etichettaClasseMateriale: string, idCaratteristicaSpessore: number) {
    return firstValueFrom(this.http.get<ReportResult>(`${environment.localApiUrl}api/Movimenti/ContenitoreReport/${orgId}?codBox=${codBox}&etichettaClasseMateriale=${etichettaClasseMateriale}&idCaratteristicaSpessore=${idCaratteristicaSpessore}`));
  }

  // Scarica il PDF dell'etichetta contenitore come file (formato pagina corretto in stampa).
  // HttpParams gestisce l'encoding di codBox e degli altri parametri (caratteri speciali).
  async getReportContenitoreFile(orgId: number, codBox: string, etichettaClasseMateriale: string, idCaratteristicaSpessore: number): Promise<Blob> {
    const params = new HttpParams()
      .set('codBox', codBox)
      .set('etichettaClasseMateriale', etichettaClasseMateriale)
      .set('idCaratteristicaSpessore', idCaratteristicaSpessore);
    return firstValueFrom(this.http.get(`${environment.localApiUrl}api/Movimenti/ContenitoreReportFile/${orgId}`,
      {params, responseType: 'blob'}));
  }

  padLeft(num: number, size: number) {
    let s = num + '';
    while (s.length < size) {
      s = '0' + s;
    }
    return s;
  }

  private getNotificationActor(): string | undefined {
    return this.organizzazioniService.codiceOperatore ?? this.authService.currentUserValue?.userName;
  }
}
